import { ReactNode, useCallback, useMemo } from "react";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { UIUnifiedPriceBreakdown, UIUnifiedPriceBreakdownItemScope, UIUnifiedPriceItem } from "@flights/types";
import useProfiler from "../../../../hooks/useProfiler";

// group FLIGHT_ADULT, FLIGHT_CHILD, FLIGHT_INFANT into FLIGHT
export type BreakdownScopeId =
  | Exclude<UIUnifiedPriceBreakdownItemScope, "FLIGHT_ADULT" | "FLIGHT_CHILD" | "FLIGHT_INFANT">
  | "FLIGHT";

export type BreakdownScope = {
  id: BreakdownScopeId;
  name: string;
  items: UIUnifiedPriceItem[];
  expandedByDefault?: boolean;
};

export type BreakdownScopeItems = UIUnifiedPriceItem[];

export type BreakdownControllerProps = UIUnifiedPriceBreakdown & {
  expandedByDefault?: Partial<Record<UIUnifiedPriceBreakdownItemScope, boolean>>;
  isSummary?: boolean;
  footerSections?: Partial<Record<BreakdownScopeId, ReactNode | string>>;
  isHungaryDepartingRyanAir: boolean;
};

export function useBreakdownController(props: BreakdownControllerProps) {
  const { price, items, addedItems, footerSections, isHungaryDepartingRyanAir, isSummary, expandedByDefault } = props;
  const i18n = useI18n();
  const { isSolo, travellersCount } = useProfiler();

  const getSectionTitle = useCallback(
    (id?: BreakdownScopeId) => {
      switch (id) {
        case "FEE":
          return i18n.trans(t("flights_price_details_fees_title"));
        case "EXTRA":
          return i18n.trans(t("flights_price_details_heading_extras"));
        case "DISCOUNT":
          return i18n.trans(t("flights_price_details_heading_discounts"));
        case "BOOKING_CHANGES":
          return i18n.trans(t("flights_price_details_heading_booking_changes"));
        default: {
          const travellerCount = travellersCount();

          return isSolo()
            ? i18n.trans(t("flights_price_details_heading_flight_solo"))
            : i18n.trans(
                t("flights_price_details_heading_flight_multi", {
                  variables: { num_travellers: travellerCount },
                  num_exception: travellerCount
                })
              );
        }
      }
    },
    [i18n, isSolo, travellersCount]
  );

  const {
    flightAdultItems,
    flightChildItems,
    flightInfantItems,
    extraItems,
    discountItems,
    feeItems,
    bookingChangesItems
  } = useMemo(() => {
    const feeItems: BreakdownScopeItems = items.filter((item) => item.scope === "FEE") || [];
    const extraItems: BreakdownScopeItems = items.filter((item) => item.scope === "EXTRA") || [];
    const discountItems: BreakdownScopeItems = items.filter((item) => item.scope === "DISCOUNT") || [];
    const flightAdultItems: BreakdownScopeItems = items.filter((item) => item.scope === "FLIGHT_ADULT") || [];
    const flightChildItems: BreakdownScopeItems = items.filter((item) => item.scope === "FLIGHT_CHILD") || [];
    const flightInfantItems: BreakdownScopeItems = items.filter((item) => item.scope === "FLIGHT_INFANT") || [];
    const bookingChangesItems: BreakdownScopeItems = items.filter((item) => item.scope === "BOOKING_CHANGES") || [];
    return {
      flightAdultItems,
      flightChildItems,
      flightInfantItems,
      extraItems,
      discountItems,
      feeItems,
      bookingChangesItems
    };
  }, [items]);

  const { extraAddedItems, discountAddedItems, feeAddedItems } = useMemo(() => {
    const feeAddedItems: BreakdownScopeItems = addedItems.filter((item) => item.scope === "FEE") || [];
    const extraAddedItems: BreakdownScopeItems = addedItems.filter((item) => item.scope === "EXTRA") || [];
    const discountAddedItems: BreakdownScopeItems = addedItems.filter((item) => item.scope === "DISCOUNT") || [];
    return { extraAddedItems, discountAddedItems, feeAddedItems };
  }, [addedItems]);

  const isExpandedByDefault = useCallback(
    (section: UIUnifiedPriceBreakdownItemScope | "FLIGHT") => {
      switch (section) {
        case "FEE":
        case "EXTRA":
        case "DISCOUNT":
        case "FLIGHT_ADULT":
        case "FLIGHT_CHILD":
        case "FLIGHT_INFANT":
        case "BOOKING_CHANGES":
          return !!expandedByDefault?.[section];
        case "FLIGHT":
          return (
            !!expandedByDefault?.FLIGHT_ADULT || !!expandedByDefault?.FLIGHT_CHILD || !!expandedByDefault?.FLIGHT_INFANT
          );
        default:
          return false;
      }
    },
    [expandedByDefault]
  );

  const sections = useMemo(() => {
    const allSections: Partial<Record<BreakdownScopeId, BreakdownScope>> = {};
    const fee = [...feeItems, ...feeAddedItems];
    const extra = [...extraItems, ...extraAddedItems];
    const discount = [...discountItems, ...discountAddedItems];
    const flight = [...flightAdultItems, ...flightChildItems, ...flightInfantItems];
    const bookingChanges = [...bookingChangesItems];
    if (fee.length)
      allSections.FEE = {
        id: "FEE",
        name: getSectionTitle("FEE"),
        items: fee,
        expandedByDefault: isExpandedByDefault("FEE")
      };
    if (extra.length)
      allSections.EXTRA = {
        id: "EXTRA",
        name: getSectionTitle("EXTRA"),
        items: extra,
        expandedByDefault: isExpandedByDefault("EXTRA")
      };
    if (flight.length)
      allSections.FLIGHT = {
        id: "FLIGHT",
        name: getSectionTitle(),
        items: flight,
        expandedByDefault: isExpandedByDefault("FLIGHT")
      };
    if (discount.length)
      allSections.DISCOUNT = {
        id: "DISCOUNT",
        name: getSectionTitle("DISCOUNT"),
        items: discount,
        expandedByDefault: isExpandedByDefault("DISCOUNT")
      };
    if (bookingChanges.length)
      allSections.BOOKING_CHANGES = {
        id: "BOOKING_CHANGES",
        name: getSectionTitle("BOOKING_CHANGES"),
        items: bookingChanges,
        expandedByDefault: isExpandedByDefault("BOOKING_CHANGES")
      };
    return allSections;
  }, [
    feeItems,
    extraItems,
    feeAddedItems,
    discountItems,
    extraAddedItems,
    flightAdultItems,
    flightChildItems,
    flightInfantItems,
    discountAddedItems,
    bookingChangesItems,
    isExpandedByDefault,
    getSectionTitle
  ]);

  const totalPriceTitle = useMemo(() => i18n.trans(t("flights_price_details_total_title")), [i18n]);

  const totalPriceFooter = useMemo(
    () =>
      isHungaryDepartingRyanAir
        ? i18n.trans(t("flights_ryanair_hungary_sr_price_single"))
        : i18n.trans(t("flights_price_details_includes_taxes")),
    [isHungaryDepartingRyanAir, i18n]
  );

  const totalPrice = useMemo(() => {
    const items = [...extraAddedItems, ...discountAddedItems, ...feeAddedItems].map((item) => item.price);
    return items.reduce((prev, current) => pricingUtils.sumPrice(prev, current), price);
  }, [price, feeAddedItems, extraAddedItems, discountAddedItems]);

  return {
    sections,
    totalPrice,
    footerSections,
    totalPriceTitle,
    totalPriceFooter,
    isSummary
  };
}
